/**
 * Display formatting.
 *
 * One layer, because `PLAN.md` §8 names timezone and date errors in totals as a
 * standing risk and the mitigation is "store UTC always, one formatting layer".
 * Every instant in the database is UTC; this is the only place it becomes words.
 *
 * ## Why flights read in local time
 *
 * A pilot remembers a flight as "Tuesday morning", not as 21:28Z the evening
 * before. UTC is right for storage and wrong for recognising your own flight in
 * a list, so everything a pilot reads is formatted in the *device's* zone and
 * labelled with it — `formatLocalDate`, `formatLocalTime`, `localZoneLabel`.
 * The UTC pair below stays for the places where the wire time itself is the
 * subject (debugging, export metadata, anything compared against a source
 * file).
 *
 * The device zone is an approximation of the flight's zone, and a deliberate
 * one: the track carries no zone of its own, and deriving one from the first
 * fix's coordinates needs a timezone-boundary dataset this app does not ship.
 * For a New Zealand pilot flying in New Zealand the two agree; for a flight
 * flown in another zone the label still says which zone is being shown, which
 * is the error this layer exists to prevent.
 *
 * `formatLocalDate`, `formatLocalTime` and `localZoneLabel` all take an
 * optional `timeZone`: an IANA zone id, or `'device'` (the default when the
 * argument is omitted entirely — every existing call site keeps reading in
 * whatever zone the browser is set to). The Settings page is the one place
 * that hands in anything else, for a pilot whose device follows a different
 * zone from the one they fly in. `formatLocalDate` also takes a `format`
 * (`'nz'`, the existing `16 Sep 2026`, or `'iso'`, `2026-09-16`) for the same
 * reason: a setting, read in exactly one place, rather than a second date
 * formatter scattered through the app.
 */

export type DateFormat = 'nz' | 'iso';

interface LocalFormatOptions {
  timeZone?: string;
}

function resolveTimeZone(timeZone: string | undefined): string | undefined {
  return timeZone === undefined || timeZone === 'device' ? undefined : timeZone;
}

/** `15 Sep 2026`, in UTC. */
export function formatUtcDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/** `21:28`, in UTC. */
export function formatUtcTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-NZ', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(date);
}

/** `16 Sep 2026` by default, or `2026-09-16` with `format: 'iso'`, in the given (or device) timezone. */
export function formatLocalDate(
  iso: string,
  options: LocalFormatOptions & { format?: DateFormat } = {},
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  const timeZone = resolveTimeZone(options.timeZone);
  if (options.format === 'iso') {
    // `en-CA` is the standard trick for locale-formatted `YYYY-MM-DD`: no
    // locale actually speaks Canadian English this way, but it is the one
    // built-in `Intl` locale that always orders year-month-day with dashes.
    return new Intl.DateTimeFormat('en-CA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    }).format(date);
  }
  return new Intl.DateTimeFormat('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  }).format(date);
}

/**
 * A stored calendar date (`YYYY-MM-DD`) shown the way every other date is.
 *
 * Deliberately no timezone: `LogbookEntry.date` and `Flight.date` are local
 * calendar days already, and pushing one through a zone conversion is how a
 * 14 March flight ends up displayed as the 13th.
 */
export function formatCalendarDate(date: string, options: { format?: DateFormat } = {}): string {
  return formatLocalDate(`${date}T00:00:00.000Z`, { ...options, timeZone: 'UTC' });
}

/**
 * `09:28`, in the given (or device) timezone — or `09:28:41` with `seconds`,
 * for a single fix rather than a flight, where two neighbouring points a few
 * seconds apart would otherwise read as the same instant.
 */
export function formatLocalTime(
  iso: string,
  options: LocalFormatOptions & { seconds?: boolean } = {},
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-NZ', {
    hour: '2-digit',
    minute: '2-digit',
    ...(options.seconds === true ? { second: '2-digit' as const } : {}),
    hour12: false,
    timeZone: resolveTimeZone(options.timeZone),
  }).format(date);
}

/**
 * `NZST` — what to print after a local time so it is never ambiguous.
 *
 * Falls back to a `UTC+12`-style offset when the platform has no short name for
 * the zone, and to the IANA id if even that is unavailable. Something is always
 * printed: a bare local time is the bug.
 */
export function localZoneLabel(iso: string, options: LocalFormatOptions = {}): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const timeZone = resolveTimeZone(options.timeZone);
  for (const timeZoneName of ['short', 'shortOffset'] as const) {
    const part = new Intl.DateTimeFormat('en-NZ', { timeZoneName, timeZone })
      .formatToParts(date)
      .find((candidate) => candidate.type === 'timeZoneName');
    if (part !== undefined && part.value !== '') {
      return part.value;
    }
  }
  return timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * `1:38` — hours and minutes, the way a logbook column reads.
 *
 * Decimal hours are what some logbooks use and what this is not: a pilot
 * transcribing into a paper book writes 1:38, and converting twice is how 1.63
 * becomes 1:63.
 */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) {
    return '—';
  }
  const totalMinutes = Math.round(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours)}:${String(minutes).padStart(2, '0')}`;
}

/** `3.4 MB`, for an import row. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '—';
  }
  if (bytes < 1024) {
    return `${String(bytes)} B`;
  }
  const units = ['kB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit] ?? 'kB'}`;
}

/** `1,425` — grouped, so a five-figure count is readable at a glance. */
export function formatCount(value: number): string {
  return new Intl.NumberFormat('en-NZ').format(value);
}

/**
 * `1.20` — decimal hours, the form every logbook column, total and message is
 * printed in.
 *
 * Here rather than in the logbook because the flight list and flight page show
 * the same hours: they used to print 3.9 hr and 3:30 for flights the grid
 * called 3.94 and 3.50, three spellings of one number across three pages.
 */
export function formatDecimalHours(hours: number): string {
  return Number.isFinite(hours) ? hours.toFixed(2) : '0.00';
}
